import { useQuery } from '@tanstack/vue-query'
import { Ref } from 'vue'

import { explorerApi } from '~/api/client'
import {
  erc721HolderListKey,
  erc721ItemActivityListKey,
  erc721ItemHolderHistoryListKey,
  erc721ItemHolderKey,
  erc721ItemProfileKey,
  erc721TransferListKey,
  erc1155HolderListKey,
  erc1155ItemActivityListKey,
  erc1155ItemHolderHistoryListKey,
  erc1155ItemHolderListKey,
  erc1155ItemProfileKey,
  erc1155TransferListKey,
  nftCardListKey,
  nftMintListKey,
  nftTradeListKey,
  nftTransferListKey
} from '~/api/service/query-keys.const'
import {
  NftAllTransferListModel, NftCardListModel,
  NftItemActivityListModel, NftItemHolderHistoryListModel,
  NftItemHolderListModel, NftItemProfileModel, NftMintListModel, NftTradeListModel,
  NftTransferListModel
} from '~/types/model/nft.model'
import { TokenHolderListModel } from '~/types/model/token.model'
import { IPagination } from '~/types/pagination.type'

import { EvmNftActivityRequest, type EvmNftActivityListInfo } from '../repository'

export const nftService = {
  useGetNftCardList: () => {
    const getNftCardList = async () => {
      const res = await explorerApi.nftController.getCardList({
        requestBody: {}
      })
      return new NftCardListModel(res)
    }
    return useQuery([ ...nftCardListKey ], getNftCardList)
  },
  /** @description
   * erc721 + erc1155 트랜스퍼 리스트 api
   * /nft/transfer 페이지에서 사용
   */
  useGetNftTransferList: (pagination: Ref<IPagination>) => {
    const getNftTransferList = async () => {
      const res = await explorerApi.nftController.getTransferList({
        requestBody: {
          page: pagination.value.page,
          size: pagination.value.rowPerPage
        }
      })
      return new NftAllTransferListModel(res as EvmNftTransferListInfo)
    }
    return useQuery([ ...nftTransferListKey, pagination ], getNftTransferList)
  },

  useGetNftTradeList: (pagination: Ref<IPagination>) => {
    const getNftTradeList = async () => {
      const res = await explorerApi.nftController.getActivityList({
        requestBody: {
          page: pagination.value.page,
          size: pagination.value.rowPerPage,
          actionType: EvmNftActivityRequest.actionType.SOLD
        }
      })
      return new NftTradeListModel(res as EvmNftActivityListInfo)
    }
    return useQuery([ ...nftTradeListKey, pagination ], getNftTradeList)
  },

  useGetNftMintList: (pagination: Ref<IPagination>) => {
    const getNftMintList = async () => {
      const res = await explorerApi.nftController.getMintList({
        requestBody: {
          page: pagination.value.page,
          size: pagination.value.rowPerPage,
          actionType: EvmNftActivityRequest.actionType.MINT
        }
      })
      return new NftMintListModel(res as EvmNftMintListInfo)
    }
    return useQuery([ ...nftMintListKey, pagination ], getNftMintList)
  },

  /** @description
   * 기존 erc721 트랜스퍼 리스트 api
   * 어드레스가 없으면 모든 erc721 트랜스퍼 리스트를 가져옴
   * /nft/erc721/[address]/transfer 페이지에서 사용
   */
  useGetNftErc721TransferList: (pagination: Ref<IPagination>, address?: string) => {
    const getNftErc721TransferList = async () => {
      const res = await explorerApi.tokenController.getErc721TokenTransferList({
        requestBody: {
          ca: address,
          page: pagination.value.page,
          size: pagination.value.rowPerPage
        }
      })
      return new NftTransferListModel(res)
    }
    return useQuery([ ...erc721TransferListKey, address, pagination ], getNftErc721TransferList)
  },
  /** @description
   * 기존 erc1155 트랜스퍼 리스트 api
   * 어드레스가 없으면 모든 erc1155 트랜스퍼 리스트를 가져옴
   * /nft/erc1155/[address]/transfer 페이지에서 사용
   */
  useGetNftErc1155TransferList: (pagination: Ref<IPagination>, address?: string) => {
    const getNftErc1155TransferList = async () => {
      const res = await explorerApi.tokenController.getErc1155TokenTransferList({
        requestBody: {
          ca: address,
          page: pagination.value.page,
          size: pagination.value.rowPerPage
        }
      })
      return new NftTransferListModel(res)
    }
    return useQuery([ ...erc1155TransferListKey, address, pagination ], getNftErc1155TransferList)
  },

  useGetNftErc721HolderList: (pagination: Ref<IPagination>, address: string) => {
    const getNftErc721HolderList = async () => {
      const res = await explorerApi.tokenController.getErc721TokenHolderList({
        requestBody: {
          ca: address,
          page: pagination.value.page,
          size: pagination.value.rowPerPage
        }
      })
      return new TokenHolderListModel(res)
    }
    return useQuery([ ...erc721HolderListKey, address, pagination ], getNftErc721HolderList)
  },

  useGetNftErc1155HolderList: (pagination: Ref<IPagination>, address: string) => {
    const getNftErc1155HolderList = async () => {
      const res = await explorerApi.tokenController.getErc1155TokenHolderList({
        requestBody: {
          ca: address,
          page: pagination.value.page,
          size: pagination.value.rowPerPage
        }
      })
      return new TokenHolderListModel(res)
    }
    return useQuery([ ...erc1155HolderListKey, address, pagination ], getNftErc1155HolderList)
  },

  useGetNftErc721ItemHolderList: (address: string, tokenId: string) => {
    const getNftErc721ItemHolderList = async () => {
      const res = await explorerApi.tokenController.getErc721ItemHolder({
        requestBody: {
          ca: address,
          tokenId,
        }
      })
      return new NftItemHolderListModel(res)
    }
    return useQuery([ ...erc721ItemHolderKey, address, tokenId ], getNftErc721ItemHolderList)
  },

  useGetNftErc1155ItemHolderList: (address: string, tokenId: string, pagination: Ref<IPagination>) => {
    const getNftErc1155ItemHolderList = async () => {
      const res = await explorerApi.tokenController.getErc1155ItemHolderList({
        requestBody: {
          ca: address,
          tokenId,
          page: pagination.value.page,
          size: pagination.value.rowPerPage
        }
      })
      return new NftItemHolderListModel(res)
    }
    return useQuery([ ...erc1155ItemHolderListKey, address, tokenId, pagination ], getNftErc1155ItemHolderList)
  },

  useGetNftErc721ItemProfile: (address: string, tokenId: string) => {
    const getNftErc721ItemProfile = async () => {
      const res = await explorerApi.tokenController.getErc721ItemProfile({
        requestBody: {
          ca: address,
          tokenId
        }
      })
      return new NftItemProfileModel(res)
    }
    return useQuery([ ...erc721ItemProfileKey, address, tokenId ], getNftErc721ItemProfile)
  },

  useGetNftErc1155ItemProfile: (address: string, tokenId: string) => {
    const getNftErc1155ItemProfile = async () => {
      const res = await explorerApi.tokenController.getErc1155ItemProfile({
        requestBody: {
          ca: address,
          tokenId
        }
      })
      return new NftItemProfileModel(res)
    }
    return useQuery([ ...erc1155ItemProfileKey, address, tokenId ], getNftErc1155ItemProfile)
  },

  useGetNftErc721ItemActivityList: (pagination: Ref<IPagination>, address: string, tokenId: string) => {
    const getNftErc721ItemActivityList = async () => {
      const res = await explorerApi.tokenController.getErc721ItemActivityList({
        requestBody: {
          ca: address,
          tokenId,
          page: pagination.value.page,
          size: pagination.value.rowPerPage
        }
      })
      return new NftItemActivityListModel(res)
    }
    return useQuery([ ...erc721ItemActivityListKey, address, tokenId, pagination ], getNftErc721ItemActivityList)
  },

  useGetNftErc1155ItemActivityList: (pagination: Ref<IPagination>, address: string, tokenId: string) => {
    const getNftErc1155ItemActivityList = async () => {
      const res = await explorerApi.tokenController.getErc1155ItemActivityList({
        requestBody: {
          ca: address,
          tokenId,
          page: pagination.value.page,
          size: pagination.value.rowPerPage
        }
      })
      return new NftItemActivityListModel(res)
    }
    return useQuery([ ...erc1155ItemActivityListKey, address, tokenId, pagination ], getNftErc1155ItemActivityList)
  },

  useGetNftErc721ItemHolderHistoryList: (pagination: Ref<IPagination>, address: string, tokenId: string) => {
    const getNftErc721ItemHolderHistoryList = async () => {
      const res = await explorerApi.tokenController.getErc721ItemHolderHistoryList({
        requestBody: {
          ca: address,
          tokenId,
          page: pagination.value.page,
          size: pagination.value.rowPerPage
        }
      })
      return new NftItemHolderHistoryListModel(res)
    }
    return useQuery([ ...erc721ItemHolderHistoryListKey, address, tokenId, pagination ], getNftErc721ItemHolderHistoryList)
  },

  useGetNftErc1155ItemHolderHistoryList: (pagination: Ref<IPagination>, address: string, tokenId: string) => {
    const getNftErc1155ItemHolderHistoryList = async () => {
      const res = await explorerApi.tokenController.getErc1155ItemHolderHistoryList({
        requestBody: {
          ca: address,
          tokenId,
          page: pagination.value.page,
          size: pagination.value.rowPerPage
        }
      })
      return new NftItemHolderHistoryListModel(res)
    }
    return useQuery([ ...erc1155ItemHolderHistoryListKey, address, tokenId, pagination ], getNftErc1155ItemHolderHistoryList)
  },
}